// src/utils/purchaseOrder.js
import { formatToCompanyTimezone, formatToUtc, getDateTimeFromSelectedDate, getYearFromCompanyDate } from "../lib/functions";
import dayjs from "dayjs";
import Big from "big.js";

const toNumber = (v, d = 0) => {
  if (v === null || v === undefined || v === "" || isNaN(v)) return d;
  const n = Number(v);
  return isFinite(n) ? n : d;
};

// Discount for a single item row: reads whichever mode is active
// (percent, the default/undefined-safe mode, or a flat amount), clamped
// so a flat amount can never exceed the item's own line total.
const getItemDiscountAmount = (item, line) => {
  if (item?.discountMode === "amount") {
    const raw = new Big(item?.discountAmt || 0);
    return raw.gt(line) ? line : raw.lt(0) ? new Big(0) : raw;
  }
  const discountPer = toNumber(item?.discount ?? item?.item_discount_per, 0);
  return line.times(discountPer).div(100);
};

export const createPurchaseOrderPayload = (state, authInfo) => {
  const {
    supplierInfo,
    itemCart,
    footerValues,
    voucherNumber,
    pur_o_id,
    entryDate,
    dueDate,
    paymentTerm,
    tagFieldId,
    conversion_rate,
    conversion_currency,
    selectedDiscountAcc,
    selectedTaxAcc,
    selectedTransportAcc,
    taxMode,
  } = state;


  let finalEntryDate = entryDate;
  if (!dayjs(entryDate).isSame(dayjs(), "day")) {
    finalEntryDate = getDateTimeFromSelectedDate(entryDate); // Merge date + current time in TZ

  } else {
    finalEntryDate = entryDate; // Use now in TZ
  }

  let dateUTC = formatToUtc(finalEntryDate);
  let companydate = formatToCompanyTimezone(dateUTC);
  const vchYear = getYearFromCompanyDate(companydate);

  let dueDays = 0;

  // if (dueDate && entryDate) {
  //   const entry = dayjs(entryDate);
  //   const due = dayjs(dueDate);

  //   if (entry.isValid() && due.isValid()) {
  //     dueDays = due.diff(entry, "day");
  //     if (dueDays < 0) dueDays = 0; 
  //   }
  // }

  const entry = entryDate
    ? dayjs(entryDate).startOf("day")
    : null;

  const due = dueDate
    ? dayjs(dueDate).startOf("day")
    : null;

  let diffDays =
    entry && due ? due.diff(entry, "day") : 0;
  if (diffDays < 0) dueDays = 0;

  console.log("entryDate12349999999", diffDays);

  return {
    pur_o_id: pur_o_id || 0,
    pur_order_no: voucherNumber,
    is_cal_type: taxMode, // "on_total" or "on_item"
    vchdate: vchYear,

    itemCart: itemCart.map((it) => {
      const isBonus = Boolean(it.bonus);

      const qty = toNumber(it.quantity ?? it.item_qty, 0);
      const rate = isBonus ? 0 : toNumber(it.rate ?? it.item_rate, 0);

      const line = new Big(qty).times(rate);

      const discountAmt = isBonus ? new Big(0) : getItemDiscountAmount(it, line);
      const afterDiscount = line.minus(discountAmt);

      // Back-computed so the printed voucher (which reads this field directly
      // in "on_item" calc mode) is correct even when the row is in amount mode.
      const discountPer = isBonus
        ? 0
        : it.discountMode === "amount"
          ? (line.eq(0) ? 0 : Number(discountAmt.div(line).times(100).toFixed(13)))
          : toNumber(it.discount ?? it.item_discount_per, 0);

      const taxPer = isBonus ? 0 : toNumber(it.tax_per ?? it.item_tax_per, 0);
      const taxAmt = afterDiscount.times(taxPer).div(100);

      return {
        warehouse_id: it.warehouse_id || 0,
        warehouse_name: it.warehouse_name || "",
        item_description: it.item_description || "",
        item_id: it.item_id,
        item_name: it.item_name || it.product,
        display_text: it.display_text || `${it.product || it.item_name} - ${voucherNumber}`,
        is_serial: it.is_serial || "no",
        item_qty: it.quantity || it.item_qty || 0,
        pur_qty: it.quantity || it.pur_qty || 0,
        qty_display: it.qty_display || `${it.quantity || 0} ${it.unit_symbol || ""}`,
        item_rate: rate,
        pur_rate: rate,
        // item_rate: it.rate || it.item_rate || 0,
        // pur_rate: it.rate || it.pur_rate || 0,
        // item_discount: it.item_discount || 0,
        // item_discount_per: it.discount || it.item_discount_per || 0,
        item_discount: Number(discountAmt.toFixed(13)),
        item_discount_per: discountPer,
        discount_mode: it.discountMode === "amount" ? "amount" : "percent",
        discount_acc_id: it.discount_acc_id || (state.selectedDiscountAcc?.acc_id || 0),
        discount_acc_name: it.discount_acc_name || "",
        // item_tax: it.item_tax || 0,
        // item_tax_per: it.tax_per || it.item_tax_per || 0,
        item_tax: Number(taxAmt.toFixed(13)),
        item_tax_per: taxPer,
        tax_acc_id: it.tax_acc_id || (state.selectedTaxAcc?.acc_id || 0),
        tax_acc_name: it.tax_acc_name || "",
        item_total: it.bonus
          ? 0
          : Number(
              (taxMode === "on_item" ? afterDiscount.plus(taxAmt) : line).toFixed(13)
            ),
        base_unit_name: it.base_unit_name || null,
        unit_name: it.unit_name || null,
        unit_symbol: it.unit_symbol || null,
        conversion: it.conversion || 1,
        per_unit_id: it.per_unit_id || 0,
        per_unit_symbol: it.per_unit_symbol || "",
        per_conversion: it.per_conversion || 1,
        retail_qty: it.retail_qty || 0,
        serials: it.serials || [],
        units: it.units || [],
      }
    }),


    masterData: {
      pur_order_no: voucherNumber,
      is_serial: "no",
      is_cal_type: taxMode === "on_total" ? "on_total" : "individual",
      created_date: entryDate ? formatToUtc(finalEntryDate) : null,
      payment_terms: paymentTerm,
      due_date: diffDays,
      // due_date: 0,
      narration: footerValues?.description || "",
      sub_total: footerValues.subtotal,
      total_discount_per: footerValues.discountPct,
      total_discount: footerValues.total_discount,
      total_discount_mode: footerValues?.discountMode === "amount" ? "amount" : "percent",
      total_tax_per: authInfo?.item_tax,
      total_tax: footerValues.totalTax,
      total_transport_cost: footerValues.total_transport_cost,
      total_amount: footerValues.grandTotal,
      discount_acc_id: selectedDiscountAcc?.acc_id || 0,
      tax_acc_id: selectedTaxAcc?.acc_id || 0,
      transport_acc_id: selectedTransportAcc?.acc_id || 0,
      other_service_cost: (footerValues?.payServiceCart || []).reduce((sum, pay) => sum.plus(pay.tran_amount || 0), new Big(0)).toNumber(),
      conversion_rate,
      conversion_currency,
      payment_terms_description: footerValues?.paymentTermsDescription  || "",
      show_terms_conditions:  footerValues?.showTermsConditions ? "yes" : "no",
    },

    supplier: {
      acc_id: supplierInfo?.acc_id,
      acc_name: supplierInfo?.acc_name,
      institution_name: supplierInfo?.institution_name,
      address: supplierInfo?.address,
      contact_no: supplierInfo?.contact_no,
    },

    // Footer values
    sub_total: footerValues.subtotal,
    total_discount: footerValues.total_discount,
    total_discount_per: footerValues.discountPct,
    total_tax: footerValues.totalTax,
    total_tax_per: footerValues.taxPct,
    total_transport_cost: footerValues.total_transport_cost,
    service_charge: footerValues.serviceCharge,
    total_amount: footerValues.grandTotal,
    paid_amount: footerValues.paidAmount || 0,
    due_amount: footerValues.dueAmount || 0,

    // Accounts mapping
    discount_acc_id: selectedDiscountAcc?.acc_id,
    tax_acc_id: selectedTaxAcc?.acc_id,
    transport_acc_id: selectedTransportAcc?.acc_id,

    // Extra
    narration: footerValues?.description || "",
    tag_field_id: tagFieldId,
    conversion_rate,
    conversion_currency,
    other_service_cost: 0,
    payment_terms: paymentTerm,
    due_date: diffDays,
    payServiceCart: footerValues?.payServiceCart || [],
  };
};
